import React, { useState, useEffect, useRef, useCallback } from 'react';
import SportbookNavbar from '../components/SportbookNavbar';
import StoreModal from '../components/StoreModal';
import SettingsModal from '../components/SettingsModal';
import Toast from '../components/Toast';
import { getSessionTicket } from '../services/api';
import './SportbookPage.css';

const SportbookPage = () => {
    const [currentMode, setCurrentMode] = useState('sc');
    const [showStore, setShowStore] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showToast, setShowToast] = useState({ show: false, message: '' });
    const [isLoading, setIsLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [sdkUrl, setSdkUrl] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [walletBalance, setWalletBalance] = useState(null);
    const btInstanceRef = useRef(null);
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);

    // Load saved mode
    useEffect(() => {
        const savedMode = localStorage.getItem('winburst-mode');
        if (savedMode) {
            setCurrentMode(savedMode);
        }
    }, []);

    // Listen for messages from parent window
    useEffect(() => {
        const handleMessage = (event) => {
            // Handle refresh wallet message from parent
            if (event.data && event.data.type === 'REFRESH_WALLET') {
                refreshWalletData(event.data.wallet);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Expose refreshWalletData method to parent
    useEffect(() => {
        window.sportbookAPI = {
            refreshWalletData: (walletData) => {
                if (walletData) {
                    setWalletBalance(walletData);
                    showToastMessage('Wallet refreshed');
                }
            }
        };

        return () => {
            delete window.sportbookAPI;
        };
    }, []);

    // Refresh wallet data method (can be called from parent)
    const refreshWalletData = useCallback((walletData) => {
        if (walletData) {
            setWalletBalance(walletData);
            showToastMessage('Wallet refreshed');
        }
    }, []);

    // Get session ticket and initialize Betby
    useEffect(() => {
        // Prevent duplicate API calls
        if (isFetchingRef.current || isInitializedRef.current) {
            return;
        }
        
        const initializeApp = async () => {
            try {
                isFetchingRef.current = true;
                setIsLoading(true);
                
                // If loaded in iframe, get sdk_url from parent query params
                // Otherwise, fetch from API
                const urlParams = new URLSearchParams(window.location.search);
                const parentSdkUrl = urlParams.get('sdk_url');
                
                let targetSdkUrl = parentSdkUrl;
                
                if (!targetSdkUrl) {
                    const gameCurrencyAlias = currentMode === 'gc' ? 'GC' : 'SC';
                    const ticketData = await getSessionTicket('test@abc.com', '7S8P', gameCurrencyAlias);
                    targetSdkUrl = ticketData.sdk_url;
                }
                
                if (targetSdkUrl) {
                    setSdkUrl(targetSdkUrl);
                    initializeBetby(targetSdkUrl);
                } else {
                    throw new Error('No SDK URL received');
                }
            } catch (error) {
                console.error('Initialization error:', error);
                showToastMessage('Failed to load sportsbook');
                setIsLoading(false);
            } finally {
                isFetchingRef.current = false;
            }
        };

        initializeApp();
        // eslint-disable-next-line react-hooks/exhaustive-deps

        // Cleanup on unmount
        return () => {
            if (btInstanceRef.current && isInitializedRef.current) {
                try {
                    btInstanceRef.current.kill();
                    isInitializedRef.current = false;
                } catch (error) {
                    console.error('Destroy error:', error);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initializeBetby = (url) => {
        try {
            const brandId = currentMode === 'gc' 
                ? '2615125477509177346' 
                : '2615125477509177345';
            
            const token = currentMode === 'gc' 
                ? "eyJ0eXAiOiJKV1QiLCJhbGciOiJFUzI1NiJ9.eyJpc3MiOiIyNjE1MTI1NDc3NTA5MTc3MzQ2IiwiYXVkIjoiMjYxNTEyNTQ3NzUwOTE3NzM0NiIsImlhdCI6MTc2NzYwODEwOCwiZXhwIjoxODAzNjA4MTA4LCJzdWIiOiJwbGF5ZXIxMjMiLCJuYW1lIjoiQWxpY2UiLCJjdXJyZW5jeSI6IlVTRCIsImxhbmciOiJlbiIsImp0aSI6Ijc1YmU5OWU2LTQzZjUtNDcxYi1hODdhLThlZDk2YmMyZTI3ZSIsIm9kZHNfZm9ybWF0IjoiZGVjaW1hbCJ9.lNP5Mz6pIQ4FW8-XcN4WPv5mnvwQAOGPKcnvIywmZr-kMfzjchIeWwu9cFpiqCwwQieio94YFERYhWFxGGJd8g"
                : "eyJ0eXAiOiJKV1QiLCJhbGciOiJFUzI1NiJ9.eyJpc3MiOiIyNjE1MTI1NDc3NTA5MTc3MzQ1IiwiYXVkIjoiMjYxNTEyNTQ3NzUwOTE3NzM0NSIsImlhdCI6MTc2NzYwODI5MCwiZXhwIjoxODAzNjA4MjkwLCJzdWIiOiJwbGF5ZXIxMjMiLCJuYW1lIjoiQWxpY2UiLCJjdXJyZW5jeSI6IlVTRCIsImxhbmciOiJlbiIsImp0aSI6ImUyODc5NDg2LTIzNDgtNGFlYi1iMDJhLWNlMTA1NzE5YzI0YyIsIm9kZHNfZm9ybWF0IjoiZGVjaW1hbCJ9.Y4TEMPCYaF7IziTFBj1Gbr0NKhNUJx5glrBthwAgyj7zk_x-u4vooU0zaKiWjvI1mPTennMw1OZ89sOrpF33Dg";

            // Initialize Betby renderer
            btInstanceRef.current = new window.BTRenderer();
            
            btInstanceRef.current.initialize({
                brand_id: brandId,
                token: token,
                themeName: 'default-table',
                lang: 'en-x-sweepstk',
                target: document.getElementById('betby'),
                betSlipOffsetTop: 0,
                betslipZIndex: 999,
                
                onTokenExpired: function() {
                    showToastMessage('Token expired. Please refresh.');
                },
                
                onLogin: function() {
                    showToastMessage('User logged in successfully');
                },
                
                onRegister: function() {
                    showToastMessage('User registered successfully');
                },
                
                onSessionRefresh: function() {
                    showToastMessage('Session refreshed');
                }
            });

            isInitializedRef.current = true;
            
            // Hide loading overlay
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);

        } catch (error) {
            console.error('Initialization error:', error);
            showToastMessage('Failed to load sportsbook');
            setIsLoading(false);
        }
    };

    const switchMode = (mode) => {
        if (mode === currentMode) return;
        
        setCurrentMode(mode);
        localStorage.setItem('winburst-mode', mode);
        
        // Reinitialize with new mode
        if (isInitializedRef.current && btInstanceRef.current) {
            try {
                btInstanceRef.current.kill();
                isInitializedRef.current = false;
                
                setTimeout(() => {
                    setIsLoading(true);
                    initializeBetby();
                }, 500);
            } catch (error) {
                console.error('Mode switch error:', error);
            }
        }
    };

    const handleBack = () => {
        // If in iframe, notify parent to close
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'SPORTBOOK_BACK' }, '*');
        } else {
            // Direct navigation fallback
            window.history.back();
        }
    };

    const showToastMessage = (message) => {
        setShowToast({ show: true, message });
        setTimeout(() => {
            setShowToast({ show: false, message: '' });
        }, 3000);
    };

    return (
        <div className="sportbook-page">
            <SportbookNavbar 
                onBackClick={handleBack}
                onOpenStore={() => setShowStore(true)}
            />
            
            <main className="main-container">
                <div className="sports-content">
                    {isLoading && (
                        <div className="loading-overlay">
                            <div className="loading-content">
                                <div className="loading-spinner"></div>
                                <div className="loading-text">Loading Sportsbook...</div>
                            </div>
                        </div>
                    )}
                    
                    <div id="betby">
                        <div className="betby-placeholder">
                            <div className="placeholder-content">
                                <h3>Loading Sportsbook...</h3>
                                <p>Initializing the application</p>
                                <div className="spinner">
                                    <i className="fas fa-spinner fa-spin fa-2x"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Floating Settings Button */}
            <button 
                className="settings-btn"
                onClick={() => setShowSettings(true)}
            >
                <i className="fas fa-cog"></i>
            </button>

            <StoreModal 
                isOpen={showStore}
                onClose={() => setShowStore(false)}
            />
            
            <SettingsModal 
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                currentMode={currentMode}
                onModeChange={switchMode}
                btInstance={btInstanceRef.current}
                showToast={showToastMessage}
            />
            
            <Toast 
                show={showToast.show}
                message={showToast.message}
                onClose={() => setShowToast({ show: false, message: '' })}
            />
        </div>
    );
};

export default SportbookPage;
